<template>
  <div class="rating-container">
    <!-- Kopfzeile: Titel, Filter, Auto-Edit-Budget -->
    <div class="ar-head">
      <div class="ar-head__title">Bewertungen</div>

      <div class="ar-filter">
        <button
          v-for="option in filterOptions"
          :key="option.value"
          type="button"
          class="ar-filter__btn"
          :class="{ 'ar-filter__btn--active': filter === option.value }"
          @click="filter = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <v-spacer />

      <!-- Tagesbudget der Auto-Bild-Edits (Klick: Limit anpassen) -->
      <div
        class="ar-budget"
        :class="{ 'ar-budget--full': usedToday >= dailyLimit }"
        @click="openLimitDialog"
      >
        <v-icon small :color="usedToday >= dailyLimit ? '#d92c2c' : '#7b5cd6'">mdi-creation</v-icon>
        Auto-Edits heute: <strong>{{ usedToday }}/{{ dailyLimit }}</strong>
        <v-icon x-small color="rgba(0,0,0,0.35)">mdi-pencil</v-icon>
      </div>
    </div>

    <StateDisplay
      class="mt-12"
      v-if="loading || error"
      :loading="loading"
      :error="error"
      :no-results="ratings.length === 0"
    />
    <div v-else>
      <h2 class="ar-section">
        <v-icon color="error">mdi-thumb-down</v-icon>
        Daumen runter
        <span class="ar-section__count">{{ filteredUnsatisfied.length }}</span>
      </h2>
      <v-row>
        <v-col
          cols="12"
          lg="4"
          md="6"
          :key="rating.taskUid"
          v-for="rating in filteredUnsatisfied"
        >
          <RatingCardAdmin
            @feedbackShared="setRatingToShared"
            :rating="rating"
          />
        </v-col>
      </v-row>

      <h2 class="ar-section mt-12">
        <v-icon color="success">mdi-thumb-up</v-icon>
        Daumen hoch
        <span class="ar-section__count">{{ filteredSatisfied.length }}</span>
      </h2>
      <v-row>
        <v-col
          cols="12"
          lg="3"
          md="4"
          sm="6"
          :key="rating.taskUid"
          v-for="rating in filteredSatisfied"
        >
          <RatingCardAdmin
            @feedbackShared="setRatingToShared"
            :rating="rating"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Limit anpassen -->
    <v-dialog v-model="showLimitDialog" max-width="360">
      <v-card class="ps-dialog pa-5">
        <div class="ar-limit-title">Tages-Limit für Auto-Bild-Edits</div>
        <div class="ar-limit-hint">
          Automatische KI-Änderungen nach 👎 + Kommentar. Manuelles
          „Jetzt ausführen" an der Karte umgeht das Limit.
        </div>
        <v-text-field
          v-model.number="limitDraft"
          type="number"
          min="0"
          solo flat dense hide-details
          background-color="#f4f5f7"
          class="mt-4"
        />
        <div class="d-flex justify-end mt-4" style="gap: 8px">
          <v-btn text small @click="showLimitDialog = false">Abbrechen</v-btn>
          <v-btn small color="success" depressed :loading="savingLimit" @click="saveLimit">
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {db} from "../../../fb";
import StateDisplay from "@/components/StateDisplay";
import {mapGetters} from "vuex";
import RatingCardAdmin from "@/components/AllRatings/RatingCardAdmin";
import {collection, doc, limit, onSnapshot, orderBy, query, setDoc, where} from "firebase/firestore";

export default {
  name: "AllRatingsAdmin",
  components: {RatingCardAdmin, StateDisplay},
  data() {
    return {
      satisfiedDocs: [],
      unsatisfiedDocs: [],
      sharedOverrides: {},
      loading: true,
      error: false,
      errorMessage: "",
      filter: "live",
      filterOptions: [
        { value: "live", label: "Live" },
        { value: "classic", label: "Klassisch" },
        { value: "all", label: "Alle" },
      ],
      // Auto-Edit-Budget (settings/autoAiEdits)
      autoEditSettings: null,
      showLimitDialog: false,
      limitDraft: 50,
      savingLimit: false,
      unsubscribers: [],
    };
  },
  computed: {
    ...mapGetters(["userNameIdMap"]),
    ratings() {
      return [...this.unsatisfiedDocs, ...this.satisfiedDocs];
    },
    unsatisfiedRatings() {
      return this.unsatisfiedDocs;
    },
    satisfiedRatings() {
      return this.satisfiedDocs;
    },
    filteredUnsatisfied() {
      return this.applyFilter(this.unsatisfiedRatings);
    },
    filteredSatisfied() {
      return this.applyFilter(this.satisfiedRatings);
    },
    dailyLimit() {
      const value = this.autoEditSettings?.dailyLimit;
      return value != null ? value : 50;
    },
    usedToday() {
      const today = new Date().toISOString().slice(0, 10);
      return this.autoEditSettings?.usage?.[today] || 0;
    },
  },
  methods: {
    applyFilter(list) {
      if (this.filter === "live") return list.filter(r => r.isLive);
      if (this.filter === "classic") return list.filter(r => !r.isLive);
      return list;
    },
    mapDoc(docSnap) {
      const task = docSnap.data();
      return {
        taskUid: task.uid,
        orderId: task.orderId,
        orderName: task.orderName,
        artistId: task.editors?.DRAW || null,
        satisfied: task.feedback.satisfied,
        timestamp: task.feedback.timestamp,
        shared: task.feedback.shared || this.sharedOverrides[task.uid] || false,
        comment: task.feedback.comment,
        autoEdit: task.feedback.autoEdit || null,
        isLive: !!task.customizationData,
        result: task.result,
        resultThumbnailUrl: task.resultThumbnailUrl,
        result2: task.result2,
        resultThumbnailUrl2: task.resultThumbnailUrl2,
        countryCode: task.countryCode,
        mockupImage: task.mockupImage,
        task: task,
      };
    },
    load() {
      const tasksCollection = collection(db, 'tasks');
      // 👎 zuerst (Fokus), 👍 nur mit Kommentar — live per onSnapshot,
      // damit Auto-Fix-Status (geplant/läuft/fertig) ohne Reload mitziehen
      const unsatisfiedQuery = query(
        tasksCollection,
        where('feedback.satisfied', '==', false),
        orderBy('feedback.timestamp', 'desc'),
        limit(100)
      );
      const satisfiedQuery = query(
        tasksCollection,
        where('feedback.hasComment', '==', true),
        where('feedback.satisfied', '==', true),
        orderBy('feedback.timestamp', 'desc'),
        limit(200)
      );

      const onError = (e) => {
        console.log(e);
        this.errorMessage = "There was an Error Loading the Ratings";
        this.error = true;
        this.loading = false;
      };

      this.unsubscribers.push(
        // Ersetzte Tasks (CANCELED, z. B. nach Änderungslink oder Kundenänderung) tragen ihr altes Feedback weiter,
        // gehören aber nicht mehr in die Liste — der Nachfolger-Task bekommt seine eigene Bewertung (Malte 15.10.)
        onSnapshot(unsatisfiedQuery, snap => {
          this.unsatisfiedDocs = snap.docs.filter(d => d.data().status !== 'CANCELED').map(d => this.mapDoc(d));
          this.loading = false;
        }, onError),
        onSnapshot(satisfiedQuery, snap => {
          this.satisfiedDocs = snap.docs.filter(d => d.data().status !== 'CANCELED').map(d => this.mapDoc(d));
          this.loading = false;
        }, onError),
        onSnapshot(doc(db, 'settings', 'autoAiEdits'), snap => {
          this.autoEditSettings = snap.data() || null;
        })
      );
    },
    getUsername(artistId) {
      const user = this.userNameIdMap.find((user) => user.uid === artistId);
      return user?.username || "unknown";
    },
    setRatingToShared(taskUid) {
      this.$set(this.sharedOverrides, taskUid, true);
      const mark = list => list.map(r => r.taskUid === taskUid ? { ...r, shared: true } : r);
      this.unsatisfiedDocs = mark(this.unsatisfiedDocs);
      this.satisfiedDocs = mark(this.satisfiedDocs);
    },
    openLimitDialog() {
      this.limitDraft = this.dailyLimit;
      this.showLimitDialog = true;
    },
    async saveLimit() {
      this.savingLimit = true;
      try {
        await setDoc(doc(db, 'settings', 'autoAiEdits'), {
          dailyLimit: Math.max(0, Number(this.limitDraft) || 0),
        }, { merge: true });
        this.showLimitDialog = false;
      } catch (e) {
        console.error("Limit speichern fehlgeschlagen", e);
      } finally {
        this.savingLimit = false;
      }
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.unsubscribers.forEach(unsub => unsub());
  },
};
</script>

<style lang="scss" scoped>
.rating-container {
  min-height: 500px;
}

.ar-head {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  margin-bottom: 22px;

  &__title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
}

.ar-filter {
  display: flex;
  background: #eef0f3;
  border-radius: 999px;
  padding: 3px;
  gap: 3px;

  &__btn {
    border: none;
    background: transparent;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    padding: 5px 16px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.55);
    transition: background 0.15s ease, color 0.15s ease;

    &--active {
      background: #fff;
      color: rgba(0, 0, 0, 0.85);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
  }
}

.ar-budget {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f4f0fd;
  border: 1px solid #e0d6f7;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
  user-select: none;

  strong {
    font-variant-numeric: tabular-nums;
  }

  &--full {
    background: #fdeaea;
    border-color: #f3c2c2;
  }
}

.ar-section {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 14px;

  &__count {
    background: #eef0f3;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    padding: 1px 10px;
    color: rgba(0, 0, 0, 0.55);
  }
}

.ar-limit-title {
  font-size: 16px;
  font-weight: 700;
}

.ar-limit-hint {
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 6px;
}
</style>
